/**
 * 采集批次详情页面
 * 展示指定批次的信息和该批次下的商品列表
 * 支持分页浏览
 */

import type { ReactElement } from 'react'
import { useState, useEffect, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  Archive,
  Download,
  Inbox,
  ChevronLeft,
  ChevronRight,
  Package,
  Calendar,
  MessageSquare,
  Search,
  X,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCrawlBatchGoods } from '@/hooks/useCrawlBatchGoods'
import { useDebounce } from '@/hooks'
import { MaterialCard } from '@/components/MaterialCard'
import { ImagePackDialog } from '@/components/workflow'
import { getCrawlBatch } from '@/services/crawlBatchService'
import { cn } from '@/lib/utils'
import { IMAGE_PACK_REF_TYPE } from '@/types/imagePack'
import type { CrawlBatch } from '@/types/crawlBatch'

/**
 * 分页组件属性
 */
interface PaginationProps {
  /** 当前页码 */
  currentPage: number
  /** 总页数 */
  totalPages: number
  /** 页码变化回调 */
  onPageChange: (page: number) => void
}

/**
 * 分页组件
 */
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps): ReactElement {
  if (totalPages <= 1) {
    return <></>
  }

  /**
   * 生成页码数组
   */
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      pages.push(totalPages)
    }

    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1.5">
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8 border-border/60 transition-colors"
        onClick={(): void => {
          onPageChange(currentPage - 1)
        }}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {getPageNumbers().map((page, index) =>
        typeof page === 'number' ? (
          <Button
            key={page}
            variant={page === currentPage ? 'default' : 'outline'}
            size="icon"
            className={cn(
              'h-8 w-8 transition-colors',
              page !== currentPage && 'border-border/60'
            )}
            onClick={(): void => {
              onPageChange(page)
            }}
          >
            {page}
          </Button>
        ) : (
          <span
            key={`ellipsis-${String(index)}`}
            className="px-1.5 text-sm text-muted-foreground"
          >
            ...
          </span>
        )
      )}

      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8 border-border/60 transition-colors"
        onClick={(): void => {
          onPageChange(currentPage + 1)
        }}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

/**
 * 加载骨架屏组件
 */
function LoadingSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl border bg-card p-3"
        >
          <div className="flex gap-3">
            <div className="h-20 w-20 rounded-lg bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 rounded bg-muted" />
              <div className="h-3 w-1/2 rounded bg-muted" />
              <div className="flex gap-2">
                <div className="h-6 w-16 rounded-full bg-muted" />
                <div className="h-6 w-16 rounded-full bg-muted" />
              </div>
            </div>
            <div className="flex gap-1">
              <div className="h-8 w-16 rounded bg-muted" />
              <div className="h-8 w-16 rounded bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * 空状态组件
 */
function EmptyState(): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center rounded-2xl border bg-card py-16"
    >
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
        <Inbox className="h-10 w-10 text-muted-foreground/50" />
      </div>
      <p className="text-lg font-medium text-muted-foreground">暂无商品</p>
      <p className="mt-1 text-sm text-muted-foreground/70">
        还没有采集到任何商品
      </p>
    </motion.div>
  )
}

/**
 * 批次概览面板组件属性
 */
interface BatchOverviewProps {
  /** 批次信息 */
  batch: CrawlBatch
}

/**
 * 批次概览面板组件
 */
function BatchOverview({ batch }: BatchOverviewProps): ReactElement {
  /**
   * 格式化日期时间
   * @param dateString ISO 日期字符串
   * @returns 格式化后的日期时间字符串
   */
  const formatDateTime = (dateString: string | null): string => {
    if (dateString === null) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border bg-card p-6"
    >
      <div className="grid gap-6 sm:grid-cols-3">
        {/* 商品总数 */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Package className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-muted-foreground">商品总数</p>
            <p className="mt-1 text-2xl font-bold tracking-tight">{batch.totalCount}</p>
          </div>
        </div>

        {/* 创建时间 */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Calendar className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-muted-foreground">创建时间</p>
            <p className="mt-1 text-sm font-medium">{formatDateTime(batch.createdAt)}</p>
          </div>
        </div>

        {/* 备注 */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <MessageSquare className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-muted-foreground">备注</p>
            <p className="mt-1 text-sm font-medium">{batch.remark || '-'}</p>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

/**
 * 采集批次详情页面组件
 * @returns 页面 ReactElement
 */
export function CrawlBatchDetailPage(): ReactElement {
  const { batchId } = useParams<{ batchId: string }>()
  const navigate = useNavigate()

  // 批次信息状态
  const [batch, setBatch] = useState<CrawlBatch | null>(null)
  const [batchLoading, setBatchLoading] = useState(true)
  const [batchError, setBatchError] = useState<string | null>(null)
  const [showPackDialog, setShowPackDialog] = useState(false)
  // 本地搜索输入状态（即时响应）
  const [searchInput, setSearchInput] = useState('')
  // 防抖后的搜索关键词
  const debouncedSearch = useDebounce(searchInput, 300)

  // 使用自定义 hook 获取商品数据
  const {
    items,
    isLoading,
    error,
    total,
    page,
    pageSize,
    keywordSearch,
    setPage,
    setKeywordSearch,
    refresh,
  } = useCrawlBatchGoods(batchId ?? '', 100)

  // 计算总页数
  const totalPages = Math.ceil(total / pageSize)
  // 判断是否正在防抖中（输入值与实际搜索值不同）
  const isDebouncing = searchInput !== keywordSearch

  /**
   * 同步防抖后的搜索值到 hook
   */
  useEffect(() => {
    if (debouncedSearch !== keywordSearch) {
      setKeywordSearch(debouncedSearch)
      setPage(1) // 搜索变化时重置到第 1 页
    }
  }, [debouncedSearch, keywordSearch, setKeywordSearch, setPage])

  /**
   * 加载批次信息
   */
  useEffect(() => {
    if (batchId === undefined || batchId === '') {
      setBatchError('批次 ID 不存在')
      setBatchLoading(false)
      return
    }

    void (async (): Promise<void> => {
      setBatchLoading(true)
      setBatchError(null)
      try {
        const batchData = await getCrawlBatch(batchId)
        setBatch(batchData)
      } catch (err) {
        setBatchError(err instanceof Error ? err.message : '获取批次信息失败')
      } finally {
        setBatchLoading(false)
      }
    })()
  }, [batchId])

  /**
   * 返回批次列表
   */
  const handleBack = useCallback((): void => {
    void navigate('/materials')
  }, [navigate])

  /**
   * 清空搜索输入
   */
  const handleClearSearch = useCallback((): void => {
    setSearchInput('')
  }, [])

  const handlePackZip = useCallback((): void => {
    setShowPackDialog(true)
  }, [])

  return (
    <div className="space-y-6">
      {/* 顶部导航栏 */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight">批次详情</h1>
        </div>
      </div>

      {/* 批次加载错误 */}
      {batchError !== null && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <p className="text-sm text-red-600 dark:text-red-400">{batchError}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={handleBack}
          >
            返回列表
          </Button>
        </div>
      )}

      {/* 批次概览面板 */}
      {batchLoading ? (
        <div className="rounded-xl border bg-card p-6">
          <div className="grid gap-6 sm:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="h-10 w-10 animate-pulse rounded-lg bg-muted" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        batch !== null && <BatchOverview batch={batch} />
      )}

      {/* 商品列表标题 */}
      {batch !== null && batchError === null && (
        <div className="flex flex-col gap-3 border-b pb-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">商品列表</h2>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild variant="outline" size="sm" className="gap-1.5">
              <Link
                to={
                  batchId !== undefined
                    ? `/records/downloads?ref_type=${IMAGE_PACK_REF_TYPE.CRAWL_BATCH}&ref_id=${batchId}`
                    : '/records/downloads'
                }
              >
                <Archive className="h-4 w-4" />
                下载中心
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handlePackZip}
              disabled={batch.totalCount <= 0}
            >
              <Download className="h-4 w-4" />
              打包ZIP下载
            </Button>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={(event): void => {
                  setSearchInput(event.target.value)
                }}
                placeholder="搜索商品标题..."
                className={cn(
                  'h-9 w-full rounded-md border border-input bg-background pl-9 pr-9 text-sm outline-none',
                  'focus:border-primary focus:ring-1 focus:ring-primary transition-all',
                  'placeholder:text-muted-foreground'
                )}
              />
              {searchInput !== '' && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {isDebouncing ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      <AnimatePresence>
        {error !== null && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-600 dark:text-red-400"
          >
            <p>{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={(): void => {
                void refresh()
              }}
            >
              重试
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 主内容区 - 商品列表 */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* 商品卡片网格 */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5">
            {items.map((material) => (
              <MaterialCard
                key={material.id}
                material={material}
                onRefresh={(): void => {
                  void refresh()
                }}
              />
            ))}
          </div>

          {/* 分页信息和控件 */}
          <div className="flex flex-col items-center gap-3 pt-2 sm:flex-row sm:justify-between">
            <p className="text-[13px] text-muted-foreground">
              共 {total} 条记录，第 {page}/{totalPages} 页
            </p>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      )}

      {/* ZIP 打包弹窗 */}
      {batchId !== undefined && batch !== null && (
        <ImagePackDialog
          open={showPackDialog}
          onOpenChange={setShowPackDialog}
          batchId={batchId}
          refType={IMAGE_PACK_REF_TYPE.CRAWL_BATCH}
          remark={batch.remark}
          totalCount={batch.totalCount}
        />
      )}
    </div>
  )
}
